import { useState, useEffect, useMemo, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { getPriceHistory } from '../lib/api';
import { cn, formatPrice } from '../lib/utils';
import type { PriceHistoryResponse, PriceHistoryDuration, Edition } from '../lib/types';

interface PriceHistoryChartProps {
  cardName: string;
  setName: string;
  cardNumber?: string;
  edition?: Edition | null;
  height?: number;
  /** Initial chart duration. Defaults to '30d'; quote-mode opens at '7d'
      since quick price quotes care about recent movement, not long-term trends. */
  initialDuration?: PriceHistoryDuration;
  /** Fired after each JustTCG fetch resolves so the parent modal can render
      a freshness caption above the chart without duplicating the fetch.
      `lastUpdated` is null on error, missing data, or when the API omits the
      field — caller should hide the caption in those cases. */
  onMetadataLoaded?: (meta: { lastUpdated: number | null }) => void;
}

type ChartDataPoint = {
  date: string;
  price: number;
  timestamp: number;
};

const DURATIONS: { value: PriceHistoryDuration; label: string }[] = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
];

export function PriceHistoryChart({
  cardName,
  setName,
  cardNumber,
  edition,
  height = 128,
  initialDuration = '30d',
  onMetadataLoaded,
}: PriceHistoryChartProps) {
  const [duration, setDuration] = useState<PriceHistoryDuration>(initialDuration);
  const [data, setData] = useState<PriceHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ref keeps the latest callback without forcing the parent to memoize it,
  // so the metadata-firing effect below can omit it from deps without going
  // stale across re-renders.
  const onMetadataLoadedRef = useRef(onMetadataLoaded);
  useEffect(() => {
    onMetadataLoadedRef.current = onMetadataLoaded;
  }, [onMetadataLoaded]);

  useEffect(() => {
    async function loadPriceHistory() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getPriceHistory(
          cardName,
          setName,
          cardNumber,
          duration,
          edition
        );

        if (response.success) {
          setData(response);
        } else {
          setError(response.error || 'Failed to load price history');
        }
      } catch (err) {
        console.error('Price history error:', err);
        setError('Unable to load price history');
      } finally {
        setIsLoading(false);
      }
    }

    loadPriceHistory();
  }, [cardName, setName, cardNumber, duration, edition]);

  // Notify parent of freshness metadata after each resolved fetch. Null on
  // any failure path or when the API omits the field so the caption hides
  // instead of holding a stale value from a prior fetch.
  useEffect(() => {
    const cb = onMetadataLoadedRef.current;
    if (!cb) return;
    // Skip the initial loading state — wait until either data or error lands
    // so the caption keeps the prior value visible during edition/duration
    // re-fetches rather than flickering off.
    if (!data && !error) return;
    if (data?.success && data.last_updated != null) {
      cb({ lastUpdated: data.last_updated });
    } else {
      cb({ lastUpdated: null });
    }
  }, [data, error]);

  // Transform price history data for the chart
  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!data?.price_history) return [];

    return data.price_history.map((point) => ({
      timestamp: point.t,
      price: point.p,
      date: new Date(point.t * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    }));
  }, [data]);

  // Get the change values for the selected duration
  const getChangeForDuration = (): { percent: number | null; abs: number | null } => {
    if (!data) return { percent: null, abs: null };

    switch (duration) {
      case '7d':
        return {
          percent: data.price_change_7d ?? null,
          abs: data.price_change_7d_abs ?? null,
        };
      case '30d':
        return {
          percent: data.price_change_30d ?? null,
          abs: data.price_change_30d_abs ?? null,
        };
      case '90d':
        return {
          percent: data.price_change_90d ?? null,
          abs: data.price_change_90d_abs ?? null,
        };
    }
  };

  const change = getChangeForDuration();
  const isPositive = change.percent !== null && change.percent > 0;
  const isNegative = change.percent !== null && change.percent < 0;
  const isNeutral = change.percent === null || change.percent === 0;

  // Calculate Y-axis domain with padding
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 10];
    const prices = chartData.map((d) => d.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.1 || 0.5;
    return [Math.max(0, min - padding), max + padding];
  }, [chartData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataPoint;
      return (
        <div className="rounded-lg bg-card px-2 py-1 shadow-lg">
          <p className="text-xs text-muted-foreground">{data.date}</p>
          <p className="text-sm font-semibold text-card-foreground">
            {formatPrice(data.price)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mt-4">
      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
        Price History
      </h3>

      <div className="rounded-lg bg-muted p-3">
        {/* Duration selector — MD3 segmented button, same track/segment
            pattern as the mode/edition toggles elsewhere in this app. */}
        <div className="mb-3 flex gap-1 rounded-full bg-card p-1">
          {DURATIONS.map((d) => (
            <button
              key={d.value}
              onClick={() => setDuration(d.value)}
              className={cn(
                'flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 ease-emphasized-decelerate',
                duration === d.value
                  ? 'bg-secondary text-secondary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center" style={{ height }}>
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-outline border-t-primary" />
          </div>
        )}

        {/* Error state */}
        {!isLoading && error && (
          <div className="flex items-center justify-center" style={{ height }}>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {/* Chart */}
        {!isLoading && !error && chartData.length > 0 && (
          <>
            <div style={{ height }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={isPositive ? '#22c55e' : isNegative ? '#ef4444' : '#9ca3af'}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={isPositive ? '#22c55e' : isNegative ? '#ef4444' : '#9ca3af'}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#49454F', fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    minTickGap={30}
                  />
                  <YAxis
                    domain={yDomain}
                    tick={{ fill: '#49454F', fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${value.toFixed(0)}`}
                    width={35}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="price"
                    stroke={isPositive ? '#22c55e' : isNegative ? '#ef4444' : '#9ca3af'}
                    strokeWidth={2}
                    fill="url(#priceGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Trend indicator */}
            <div className="mt-3 flex items-center justify-center gap-2">
              {isPositive && (
                <TrendingUp className="h-4 w-4 text-green-500" />
              )}
              {isNegative && (
                <TrendingDown className="h-4 w-4 text-red-400" />
              )}
              {isNeutral && (
                <Minus className="h-4 w-4 text-muted-foreground" />
              )}
              <span
                className={cn(
                  'text-sm font-semibold',
                  isPositive && 'text-green-500',
                  isNegative && 'text-red-400',
                  isNeutral && 'text-muted-foreground'
                )}
              >
                {change.percent !== null ? (
                  <>
                    {change.percent > 0 ? '+' : ''}
                    {change.percent.toFixed(1)}%
                    {change.abs !== null && (
                      <span className="ml-1 text-xs opacity-75">
                        ({change.abs > 0 ? '+' : ''}
                        {formatPrice(change.abs)})
                      </span>
                    )}
                  </>
                ) : (
                  'No change data'
                )}
              </span>
            </div>
          </>
        )}

        {/* No data state */}
        {!isLoading && !error && chartData.length === 0 && (
          <div className="flex items-center justify-center" style={{ height }}>
            <p className="text-sm text-muted-foreground">No price history available</p>
          </div>
        )}
      </div>
    </div>
  );
}
